import React from "react";
import {
  MessageCircle,
  Bot,
  Cpu,
  Sparkles,
  Zap,
  Code2,
  Brain,
  Cloud,
} from "lucide-react";
import { RevealText } from "./RevealText";
import { Magnetic } from "./Magnetic";

const WHATSAPP_URL = "https://wa.me/6285190844109";

// Position + delay metadata for floating background icons
const FLOATERS = [
  { Icon: MessageCircle, top: "12%", left: "8%", size: 28, delay: "0s", dur: "7s" },
  { Icon: Bot, top: "22%", left: "82%", size: 32, delay: "1.4s", dur: "8s" },
  { Icon: Sparkles, top: "70%", left: "12%", size: 22, delay: "0.8s", dur: "6s" },
  { Icon: Cpu, top: "78%", left: "78%", size: 26, delay: "2s", dur: "9s" },
  { Icon: Zap, top: "40%", left: "92%", size: 22, delay: "0.4s", dur: "6.5s" },
  { Icon: Code2, top: "60%", left: "4%", size: 24, delay: "1.8s", dur: "7.5s" },
  { Icon: Brain, top: "8%", left: "48%", size: 26, delay: "2.4s", dur: "8.5s" },
  { Icon: Cloud, top: "85%", left: "44%", size: 28, delay: "1.1s", dur: "9.5s" },
  { Icon: MessageCircle, top: "30%", left: "30%", size: 18, delay: "3s", dur: "6s" },
  { Icon: Sparkles, top: "55%", left: "68%", size: 18, delay: "2.6s", dur: "7s" },
];

const WhatsAppGlyph = ({ className = "" }) => (
  // Official WhatsApp glyph (single-color, fills currentColor)
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    fill="currentColor"
  >
    <path d="M19.077 4.928A9.94 9.94 0 0 0 12.005 2c-5.5 0-9.96 4.46-9.96 9.96 0 1.76.46 3.47 1.34 4.98L2 22l5.22-1.36a9.94 9.94 0 0 0 4.78 1.22h.005c5.5 0 9.96-4.46 9.96-9.96 0-2.66-1.04-5.16-2.92-7.04zM12.01 20.13h-.004a8.27 8.27 0 0 1-4.21-1.15l-.3-.18-3.1.81.83-3.02-.2-.31a8.26 8.26 0 0 1-1.27-4.4c0-4.57 3.72-8.29 8.29-8.29 2.21 0 4.29.86 5.86 2.43a8.23 8.23 0 0 1 2.43 5.87c0 4.57-3.72 8.29-8.32 8.29zm4.55-6.21c-.25-.13-1.47-.72-1.7-.81-.23-.08-.4-.13-.56.13-.17.25-.65.81-.79.97-.15.17-.29.19-.54.06-.25-.13-1.05-.39-2-1.23a7.5 7.5 0 0 1-1.39-1.72c-.15-.25-.02-.39.11-.51.11-.11.25-.29.38-.43.13-.15.17-.25.25-.42.08-.17.04-.31-.02-.43-.06-.13-.56-1.36-.77-1.86-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.84-.86 2.05 0 1.21.88 2.38 1 2.55.13.17 1.74 2.66 4.22 3.73.59.25 1.05.4 1.41.51.59.19 1.13.16 1.55.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.15-1.18-.06-.11-.23-.17-.48-.3z" />
  </svg>
);

export const CTABanner = () => {
  return (
    <section
      data-testid="cta-banner-section"
      className="relative overflow-hidden bg-[#05030A] py-24 sm:py-32"
    >
      {/* Pulsing neon purple radial */}
      <div className="cta-radial pointer-events-none absolute inset-0" />
      {/* Soft grid */}
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />

      {/* Floating background icons */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {FLOATERS.map(({ Icon, top, left, size, delay, dur }, i) => (
          <span
            key={i}
            className="cta-floater absolute text-[#B026FF]/45"
            style={{
              top,
              left,
              animationDelay: delay,
              animationDuration: dur,
            }}
          >
            <Icon size={size} strokeWidth={1.6} />
          </span>
        ))}
      </div>

      <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#B026FF]/30 bg-[#B026FF]/10 backdrop-blur-md text-[10px] uppercase tracking-[0.22em] text-white/85 mb-7">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)] animate-pulse" />
          Online · replies within minutes
        </div>

        <h2
          data-testid="cta-headline"
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]"
        >
          <RevealText>Ready to Automate</RevealText>{" "}
          <RevealText delay={220} className="neon-text">
            Your Success?
          </RevealText>
        </h2>

        <p
          data-testid="cta-subheadline"
          className="mt-6 text-base sm:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed"
        >
          Let&apos;s discuss your project and build intelligent systems for your
          business.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Magnetic strength={0.28}>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp-btn"
              data-cursor="hover"
              className="cta-wa-btn group relative inline-flex items-center gap-3 rounded-full bg-white text-[#0A0510] font-semibold text-sm sm:text-base px-7 h-14 transition-all duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] hover:bg-[#25D366] hover:text-white hover:scale-[1.05] hover:shadow-[0_0_0_1px_rgba(37,211,102,0.6),0_0_40px_rgba(37,211,102,0.55),0_0_80px_rgba(37,211,102,0.35)]"
            >
              <span className="cta-wa-icon flex w-9 h-9 items-center justify-center rounded-full bg-[#25D366] text-white transition-all duration-300 group-hover:bg-white group-hover:text-[#25D366] group-hover:rotate-[-6deg]">
                <WhatsAppGlyph className="w-5 h-5" />
              </span>
              Chat on WhatsApp
              <span className="text-xs opacity-60 group-hover:opacity-90 transition">
                →
              </span>
            </a>
          </Magnetic>

          <a
            href="mailto:hello@flowbase.studio"
            data-testid="cta-email-btn"
            data-cursor="hover"
            className="text-sm text-white/60 hover:text-white underline-offset-4 hover:underline transition-colors"
          >
            or email us directly
          </a>
        </div>

        {/* Trust micro-stats */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 font-mono-ui text-[10px] uppercase tracking-widest text-white/45">
          <span>· 1,200+ projects shipped</span>
          <span>· 24h response sla</span>
          <span>· 100% money-back guarantee</span>
        </div>
      </div>
    </section>
  );
};
